import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
}

interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface TelegramContextType {
  isReady: boolean;
  isTelegram: boolean;
  user: TelegramUser | null;
  initData: string | null;
  platform: string | null;
  viewportHeight: number | null;
  viewportStableHeight: number | null;
  safeAreaInset: Insets;
  contentSafeAreaInset: Insets;
  expand: () => void;
  close: () => void;
  showMainButton: (text: string, onClick: () => void) => void;
  hideMainButton: () => void;
  showBackButton: (onClick: () => void) => void;
  hideBackButton: () => void;
}

const TelegramContext = createContext<TelegramContextType | null>(null);

const ZERO_INSETS: Insets = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

// Mock user for development - must match backend seed data
const MOCK_USER: TelegramUser = {
  id: 'dev_user_123' as any, // String to match backend mock
  first_name: 'Dev',
  last_name: 'Tester',
  username: 'dev_tester',
};

function normalizeInsets(insets?: Partial<Insets> | null): Insets {
  return {
    top: Math.max(0, Number(insets?.top) || 0),
    right: Math.max(0, Number(insets?.right) || 0),
    bottom: Math.max(0, Number(insets?.bottom) || 0),
    left: Math.max(0, Number(insets?.left) || 0),
  };
}

function setInsetVariables(prefix: string, insets: Insets) {
  const root = document.documentElement;
  root.style.setProperty(`${prefix}-top`, `${insets.top}px`);
  root.style.setProperty(`${prefix}-right`, `${insets.right}px`);
  root.style.setProperty(`${prefix}-bottom`, `${insets.bottom}px`);
  root.style.setProperty(`${prefix}-left`, `${insets.left}px`);
}

function isIOSMiniApp(platform?: string | null) {
  const normalizedPlatform = platform?.toLowerCase() || '';
  return (
    normalizedPlatform.includes('ios') ||
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function TelegramProvider({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const [isTelegram, setIsTelegram] = useState(false);
  const [user, setUser] = useState<TelegramUser | null>(null);
  const [initData, setInitData] = useState<string | null>(null);
  const [platform, setPlatform] = useState<string | null>(null);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [viewportStableHeight, setViewportStableHeight] = useState<number | null>(null);
  const [safeAreaInset, setSafeAreaInset] = useState<Insets>(ZERO_INSETS);
  const [contentSafeAreaInset, setContentSafeAreaInset] = useState<Insets>(ZERO_INSETS);

  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    const setIOSOffset = () => {
      const offset = isIOSMiniApp(webApp?.platform) ? 20 : 0;
      document.documentElement.style.setProperty('--app-ios-extra-offset', `${offset}px`);
    };

    const applyViewport = () => {
      const height = Number(webApp?.viewportHeight) || window.visualViewport?.height || window.innerHeight;
      const stableHeight = Number(webApp?.viewportStableHeight) || height;
      setViewportHeight(height);
      setViewportStableHeight(stableHeight);
      document.documentElement.style.setProperty('--app-viewport-height', `${height}px`);
      document.documentElement.style.setProperty('--app-viewport-stable-height', `${stableHeight}px`);
    };

    const applyInsets = () => {
      const safeInsets = normalizeInsets(webApp?.safeAreaInset);
      const contentInsets = normalizeInsets(webApp?.contentSafeAreaInset);

      setSafeAreaInset(safeInsets);
      setContentSafeAreaInset(contentInsets);
      setInsetVariables('--app-tg-safe-area-inset', safeInsets);
      setInsetVariables('--app-tg-content-safe-area-inset', contentInsets);
    };

    const applyBottomBarColor = () => {
      if (webApp?.isVersionAtLeast?.('7.10')) {
        webApp.setBottomBarColor?.('#090D0E');
      }
    };
    
    if (webApp && webApp.initData) {
      // Running inside Telegram
      setIsTelegram(true);
      setInitData(webApp.initData);
      setPlatform(webApp.platform || null);
      
      if (webApp.initDataUnsafe?.user) {
        setUser(webApp.initDataUnsafe.user);
      }
      
      webApp.ready();
      webApp.expand();
      try {
        webApp.requestFullscreen?.();
      } catch {
        // Fullscreen is optional and platform/version dependent.
      }
      applyViewport();
      applyInsets();
      setIOSOffset();
      applyBottomBarColor();
      setIsReady(true);

      webApp.onEvent?.('viewportChanged', applyViewport);
      webApp.onEvent?.('safeAreaChanged', applyInsets);
      webApp.onEvent?.('contentSafeAreaChanged', applyInsets);
      webApp.onEvent?.('fullscreenChanged', applyInsets);
      webApp.onEvent?.('themeChanged', applyBottomBarColor);

      return () => {
        webApp.offEvent?.('viewportChanged', applyViewport);
        webApp.offEvent?.('safeAreaChanged', applyInsets);
        webApp.offEvent?.('contentSafeAreaChanged', applyInsets);
        webApp.offEvent?.('fullscreenChanged', applyInsets);
        webApp.offEvent?.('themeChanged', applyBottomBarColor);
      };
    } else {
      // Running in browser (dev mode)
      setIsTelegram(false);
      setUser(MOCK_USER);
      setPlatform('browser');
      applyViewport();
      applyInsets();
      setIOSOffset();
      setIsReady(true);
    }
  }, []);

  const expand = useCallback(() => {
    window.Telegram?.WebApp?.expand();
  }, []);

  const close = useCallback(() => {
    window.Telegram?.WebApp?.close();
  }, []);

  const showMainButton = useCallback((text: string, onClick: () => void) => {
    const mainButton = window.Telegram?.WebApp?.MainButton;
    if (mainButton) {
      mainButton.text = text;
      mainButton.onClick(onClick);
      mainButton.show();
    }
  }, []);

  const hideMainButton = useCallback(() => {
    window.Telegram?.WebApp?.MainButton?.hide();
  }, []);

  const showBackButton = useCallback((onClick: () => void) => {
    const backButton = window.Telegram?.WebApp?.BackButton;
    if (backButton) {
      backButton.onClick(onClick);
      backButton.show();
    }
  }, []);

  const hideBackButton = useCallback(() => {
    window.Telegram?.WebApp?.BackButton?.hide();
  }, []);

  return (
    <TelegramContext.Provider
      value={{
        isReady,
        isTelegram,
        user,
        initData,
        platform,
        viewportHeight,
        viewportStableHeight,
        safeAreaInset,
        contentSafeAreaInset,
        expand,
        close,
        showMainButton,
        hideMainButton,
        showBackButton,
        hideBackButton,
      }}
    >
      {children}
    </TelegramContext.Provider>
  );
}

export function useTelegram() {
  const context = useContext(TelegramContext);
  if (!context) {
    throw new Error('useTelegram must be used within a TelegramProvider');
  }
  return context;
}
